import { findKey, inRange } from "nodash"

const getMonthDifference = (startDate: Date, endDate: Date) => {
    return (
        endDate.getMonth() -
        startDate.getMonth() +
        12 * (endDate.getFullYear() - startDate.getFullYear())
    )
}

// using types to spell out exactly what the numbers mean
type IncludedRangeStart = number
type ExcludedRangeEnd = number
type TranslationKeyRangeNumberMap = Record<
    string,
    // if end is undefined, then anything greater than the start value will be matched
    [IncludedRangeStart, ExcludedRangeEnd] | [IncludedRangeStart]
>

// important: these two maps need to have an equivalent key in the public/locales/[en]/common.json folder
// so that the translator method can actually look up the string equivalent for it!
const dayCountTranslationMap: TranslationKeyRangeNumberMap = {
    days_ago_0: [0, 1],
    days_ago_1: [1, 2],
    days_ago_7: [2, 8],
    days_ago_14: [8, 15],
    days_ago_21: [15, 22],
    days_ago_28: [22, 31],
}

const monthCountTranslationMap: TranslationKeyRangeNumberMap = {
    months_ago_1: [1, 2],
    months_ago_2: [2, 3],
    months_ago_3: [3, 4],
    months_ago_many: [4],
}

const millisecondsToDaysDivisor = 1000 * 60 * 60 * 24

const pluralize = (value: number, unit: string) => {
    if (value === 1) {
        return `${value} ${unit}`
    }
    return `${value} ${unit}s`
}

/**
 * a method to turn a given date string into a more human readable format via translation keys
 *
 * @todo -- exhaustive test cases please!!
 * @todo -- not really interested in the future dates atm, but if used, make sure it works first before relying on it...
 *
 * @param translator - generally taken from useTranslation
 * @param isoDateString - the format that is defaulted to in the dynamoDB tables
 * @returns usually a string, or if no translation found the string key that it was trying to translate
 */
export function translateIsoDateStringToHumanReadable(
    translator: (value: string, options?: { count: number }) => string,
    isoDateString1?: string,
    isoDateString2?: string
): string | undefined {
    // to ensure we have truely same nows, we must only initialize it once!
    const isoNow = new Date().toISOString()
    // then set the dates from the strings
    const exactDate1 = new Date(isoDateString1 || isoNow)
    const exactDate2 = new Date(isoDateString2 || isoNow)

    const timeDifference = exactDate2.getTime() - exactDate1.getTime()

    //today
    if (Math.abs(timeDifference) < millisecondsToDaysDivisor) {
        const minutes = Math.ceil(Math.abs(timeDifference) / 1000 / 60)
        if (minutes === 0) {
            return translator("days_ago_0")
        }
        if (minutes < 60) {
            return pluralize(minutes, "minute")
        }
        const hours = Math.ceil(minutes / 60)
        return pluralize(hours, "hour")
    }

    const dayCountDifference = Math.ceil(Math.abs(timeDifference / millisecondsToDaysDivisor))
    if (timeDifference < 0) {
        // in theory, the string key would look something like "{{count}} days from today"
        return translator("days_from", { count: dayCountDifference })
    }

    const dayCountTranslationKey = findKey(dayCountTranslationMap, ([start, end]) => {
        if (end === undefined) {
            return start === dayCountDifference
        }

        return inRange(dayCountDifference, start, end)
    })

    if (dayCountTranslationKey) {
        return translator(dayCountTranslationKey)
    }

    const monthDifference = getMonthDifference(exactDate1, exactDate2)
    const monthCountTranslationKey = findKey(monthCountTranslationMap, ([start, end]) => {
        if (end === undefined) {
            return start <= monthDifference
        }

        return inRange(monthDifference, start, end)
    })

    if (monthCountTranslationKey) {
        return translator(monthCountTranslationKey)
    }

    console.warn("Missing a translation key for ", {
        exactDate1,
        exactDate2,
        dayCountDifference,
        monthDifference,
    })

    return undefined
}
